import type { MapDef } from "../../engine/world/TileMap";

/**
 * Maps are ASCII art plus a legend. Every row must be the same length; the
 * TileMap constructor complains loudly if one is not.
 */

const town: MapDef = {
  id: "town",
  name: "Aldreth",
  title: "Aldreth, a village at the edge of the moor",
  sky: 0x8fd0ff,
  fog: { near: 20, far: 42 },
  ambient: 1.15,
  music: "town",
  spawn: { x: 11, y: 12, facing: "down" },
  legend: {
    T: "thicket",
    "*": "grass",
    ",": "cobble",
    ".": "town-floor",
    "#": "wall",
    "^": "roof",
    D: "door",
    I: "doorway",
    W: "well",
    S: "signpost",
    g: "garden",
  },
  rows: [
    "TTTTTTTTTTTTTTTTTTTTTTTT",
    "T**********************T",
    "T*^^^^^*****#####*****gT",
    "T*#####*****#####*****gT",
    "T*#DI##*****###D###***gT",
    "T*.....*****.......**..T",
    "T*....,,,,,,,,,,,,,,,..T",
    "T*....,,,,,W,,,,,,,,,..T",
    "T*....,,,,,,,,,,,,,,,..T",
    "T*^^^^,,,,,,,,,,,^^^^^.T",
    "T*####,,,,,,,,,,,#####.T",
    "T*#DD#,,,,,,,,,,,#DD##.T",
    "T*....,,,,,,,,,,,.....ST",
    "T*****,,,,,,,,,,,******T",
    "TTTTTTTTT,,,,,TTTTTTTTTT",
    "TTTTTTTTT,,,,,TTTTTTTTTT",
  ],
  exits: [
    ...[9, 10, 11, 12, 13].map((x) => ({ x, y: 15, to: "overworld", toX: 16, toY: 3, facing: "down" as const })),
    { x: 4, y: 4, to: "inn", toX: 5, toY: 4, facing: "down" as const },
  ],
  npcs: [
    {
      x: 11,
      y: 10,
      facing: "down",
      name: "Elder Maud",
      appearance: { tunic: 0x8a6a9a, hair: 0xdddddd, trim: 0xffd23f, hat: "hood", hatColor: 0x8a6a9a, scale: 0.95 },
      lines: [
        "Elder Maud: You have the look of someone about to do something unwise.",
        "Elder Maud: The cave east of the moor has been breathing smoke for a week.\nSomething is nesting in it.",
        "Elder Maud: Rest at the inn before you go. It is what the wise do.",
      ],
      movement: "spin",
    },
    {
      x: 5,
      y: 5,
      facing: "down",
      name: "Innkeeper",
      tag: "inn-barker",
      appearance: { tunic: 0xd8a05a, hair: 0x3b2a1a, trim: 0xffffff, scale: 0.98 },
      lines: ["Innkeeper: Beds inside, traveller. Ten gold and no questions."],
    },
    {
      x: 16,
      y: 6,
      facing: "down",
      name: "Guard",
      appearance: { tunic: 0x4a5a7a, trim: 0xc0c8d8, hat: "helm", hatColor: 0x8a94a8, sword: true, shield: true },
      lines: [
        "Guard: Gate's south. Moor's past that. Wolves are past that.",
        "Guard: If you see a slime, hit it. That's the whole trick.",
      ],
    },
    {
      x: 8,
      y: 12,
      facing: "right",
      name: "Child",
      appearance: { tunic: 0x5ac0a0, hair: 0x7a4a22, pants: 0x8a6a3a, scale: 0.72 },
      lines: ["Child: I'm going to be a hero too! I've been practising on the well."],
      movement: "wander",
    },
    {
      x: 19,
      y: 12,
      facing: "left",
      name: "Sister Elowen",
      tag: "recruit-cleric",
      hiddenByFlag: "cleric-joined",
      appearance: {
        tunic: 0xe8e4d8,
        trim: 0xffd23f,
        pants: 0x9a8a70,
        hair: 0x3b2a1a,
        hat: "hood",
        hatColor: 0xe8e4d8,
        staff: true,
        scale: 0.97,
      },
      lines: ["Sister Elowen: Someone has to keep you alive out there."],
    },
    {
      x: 22,
      y: 12,
      name: "Signpost",
      tag: "sign",
      lines: ["The sign reads:\n  SOUTH — the moor, and worse.\n  Please close the gate."],
    },
  ],
  chests: [{ x: 21, y: 5, item: "herb" }],
};

const inn: MapDef = {
  id: "inn",
  name: "The Weary Hart",
  title: "The Weary Hart",
  sky: 0x1a1420,
  ambient: 1.0,
  ambientColor: 0x6a5a50,
  sunColor: 0xffd0a0,
  sunIntensity: 1.1,
  fillColor: 0xffc890,
  fillIntensity: 0.5,
  music: "town",
  legend: {
    "#": "wall",
    ".": "inn-floor",
    b: "bed",
    X: "doorway",
  },
  rows: [
    "###########",
    "#.........#",
    "#.bb...bb.#",
    "#.bb...bb.#",
    "#.........#",
    "#....X....#",
    "###########",
  ],
  exits: [{ x: 5, y: 5, to: "town", toX: 4, toY: 5, facing: "down" }],
  npcs: [
    {
      x: 7,
      y: 1,
      facing: "down",
      name: "Innkeeper",
      tag: "inn",
      appearance: { tunic: 0xd8a05a, hair: 0x3b2a1a, trim: 0xffffff, scale: 0.98 },
      lines: ["Innkeeper: Welcome to the Weary Hart."],
    },
  ],
};

const overworld: MapDef = {
  id: "overworld",
  name: "Ashen Moor",
  title: "The Ashen Moor",
  sky: 0x7fc4ff,
  fog: { near: 24, far: 50 },
  ambient: 1.1,
  music: "field",
  spawn: { x: 16, y: 3, facing: "down" },
  legend: {
    M: "mountain",
    "^": "hill",
    T: "forest",
    t: "pines",
    ".": "grass",
    f: "grass-flowers",
    ",": "path",
    "~": "water",
    s: "shallows",
    r: "rocks",
    V: "doorway",
    C: "doorway",
  },
  rows: [
    "MMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMM",
    "M..............................M",
    "M....TTT........VV.........TTT.M",
    "M...TTTT........,,........TTTT.M",
    "M....TT.........,,.........TT..M",
    "M..~~~..........,,.............M",
    "M.~~~~~.........,,.....ttt.....M",
    "M.~~~~~~~.......,,....ttttt....M",
    "M..~~~~~s.......,,.....ttt.....M",
    "M...~~ss........,,.............M",
    "M....ss.........,,.............M",
    "M......fff......,,........rrr..M",
    "M.....fffff.....,,,,,,,,,,,,...M",
    "M......fff.................,...M",
    "M..........TTT.............,...M",
    "M.........TTTTT............,...M",
    "M..^^^...TTTTT.............,...M",
    "M.^^^^^....TTT..........MMM,MM.M",
    "M.^^^^^.................MMMCMM.M",
    "M.........TTT...........MMMMM..M",
    "M..............................M",
    "M....~~~.......................M",
    "M...~~~~~......................M",
    "MMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMM",
  ],
  encounter: {
    maxActive: 7,
    respawn: 30,
    groups: [
      { monsters: [{ id: "slime", count: [1, 3] }], weight: 5 },
      { monsters: [{ id: "red-slime" }], weight: 3 },
      { monsters: [{ id: "wolf", count: [1, 2] }], weight: 3 },
      { monsters: [{ id: "bat", count: [1, 2] }], weight: 3 },
      { monsters: [{ id: "plant" }], weight: 2 },
      { monsters: [{ id: "slime", count: 2 }, { id: "wolf" }], weight: 2 },
    ],
  },
  exits: [
    { x: 16, y: 2, to: "town", toX: 11, toY: 14, facing: "up" },
    { x: 17, y: 2, to: "town", toX: 11, toY: 14, facing: "up" },
    { x: 27, y: 18, to: "cave", toX: 17, toY: 1, facing: "down" },
  ],
  npcs: [
    {
      x: 15,
      y: 12,
      facing: "down",
      name: "Wanderer",
      appearance: { tunic: 0x6a6a7a, hair: 0x2a2a2a, pants: 0x3a3a44, hat: "cap", hatColor: 0x4a4a5a },
      lines: [
        "Wanderer: The path east ends at a hole in the mountain.",
        "Wanderer: I got as far as the graves. Then I got sensible.",
      ],
      movement: "wander",
    },
  ],
  chests: [
    { x: 6, y: 11, item: "copper-sword" },
    { x: 12, y: 16, gold: 45 },
  ],
};

const cave: MapDef = {
  id: "cave",
  name: "The Smoking Cave",
  title: "The Smoking Cave",
  sky: 0x0a0810,
  fog: { near: 10, far: 30 },
  ambient: 0.9,
  ambientColor: 0x5a5480,
  sunColor: 0x9fb0ff,
  sunIntensity: 0.8,
  fillColor: 0xff9a5a,
  fillIntensity: 0.5,
  music: "cave",
  legend: {
    "#": "cave-wall",
    ".": "cave-floor",
    c: "cave-crystal",
    t: "cave-torch",
    g: "graves",
    L: "lava",
    E: "doorway",
  },
  rows: [
    "####################",
    "#....########.....E#",
    "#.t..########..t..##",
    "#....########.....##",
    "#..#############..##",
    "#..#############..##",
    "#..###.......###..##",
    "#..###.......###..##",
    "#.............c...##",
    "#..###.......###..##",
    "#..###..ggg..###..##",
    "#..###.......###..##",
    "#..#############..##",
    "#.....LL...c......##",
    "#.................##",
    "####################",
  ],
  encounter: {
    maxActive: 6,
    respawn: 35,
    groups: [
      { monsters: [{ id: "bat", count: [1, 3] }], weight: 4 },
      { monsters: [{ id: "skeleton", count: [1, 2] }], weight: 4 },
      { monsters: [{ id: "ghost" }], weight: 3 },
      { monsters: [{ id: "golem" }], weight: 2 },
      { monsters: [{ id: "knight" }], weight: 2 },
      { monsters: [{ id: "skeleton" }, { id: "bat", count: 2 }], weight: 2 },
    ],
  },
  exits: [{ x: 18, y: 1, to: "overworld", toX: 27, toY: 17, facing: "up" }],
  npcs: [
    {
      x: 14,
      y: 13,
      facing: "left",
      name: "Whelp",
      tag: "boss-dragon",
      monster: { kind: "dragon", color: 0x2f7a4a, accent: 0xd8e05a, scale: 1.0 },
      hiddenByFlag: "dragon-slain",
      lines: [],
      turnsToPlayer: false,
    },
  ],
  chests: [
    { x: 2, y: 13, item: "chain-mail" },
    { x: 9, y: 10, item: "elixir" },
    { x: 11, y: 6, item: "iron-blade" },
  ],
};

export const MAPS: MapDef[] = [town, inn, overworld, cave];
